import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import { useAppSelector } from '@store';
import { productsTotalSelector } from '@store/slices/cart';

import {
  LoadExternalScript, LoadExternalStyle, LoadInlineScript, updateCartCookies,
} from '@utils';
import { AEM_RESOURCE_BASE_URL } from '@config/constants';
import { getHeaderFooter, HeaderFooter } from './getHeaderFooter';
import { triggerAEMHeaderCartIcon } from './utils';

type UseAEMResourceState = Pick<HeaderFooter, 'header' | 'footer'>;
type UseAEMResource = UseAEMResourceState & {
  stylesLoaded: boolean;
  onMounted: () => void;
};

const initialState: UseAEMResource = {
  header: '',
  footer: '',
  stylesLoaded: false,
  onMounted: () => {},
};

const AEMResourceContext = createContext(initialState);
let loaded = false;

export const AEMResourceProvider = ({ children }: { children: React.ReactNode }) => {
  const [stylesLoaded, setStylesLoaded] = useState(false);
  const [state, setState] = useState<UseAEMResourceState>({
    header: initialState.header,
    footer: initialState.footer,
  });

  const [isSpectrabaseHeader, setIsSpectrabaseHeader] = useState(false);
  const { productsTotal } = useAppSelector(productsTotalSelector);

  const updateStyles = (styles: NodeListOf<HTMLStyleElement> | []) => {
    let stylesCount = styles.length;
    if (stylesCount === 0) setStylesLoaded(true);

    const onload = () => {
      stylesCount -= 1;
      if (stylesCount <= 0) setStylesLoaded(true);
    };

    styles.forEach((style) => {
      const styleSrc = style.getAttribute('href');
      const isExternalStyle = styleSrc && styleSrc.startsWith('https');

      if (!isExternalStyle) LoadExternalStyle(AEM_RESOURCE_BASE_URL + styleSrc, onload);
      if (isExternalStyle) onload();
    });
  };

  const updateScripts = (scripts: NodeListOf<HTMLScriptElement> | []) => {
    scripts.forEach((script) => {
      const scriptSrc = script.getAttribute('src');

      if (!scriptSrc) {
        LoadInlineScript(script.innerHTML);
        return;
      }

      // Check if it's a wiley clientlib script
      const clientlibsIndex = scriptSrc.indexOf('/etc.clientlibs/');

      if (clientlibsIndex === -1) {
        LoadExternalScript(scriptSrc);
        return;
      }

      const externalScriptUrl = AEM_RESOURCE_BASE_URL + scriptSrc.slice(clientlibsIndex);
      LoadExternalScript(externalScriptUrl);
    });
  };

  const loadCSS = (styles: string[]) => {
    let stylesCount = styles.length;
    if (stylesCount === 0) setStylesLoaded(true);
    const onload = () => {
      stylesCount -= 1;
      if (stylesCount <= 0) setStylesLoaded(true);
    };

    styles.forEach((styleSrc) => {
      LoadExternalStyle(styleSrc, onload);
    });
  };

  // Function to extract and load scripts from header/footer HTML content
  const extractAndLoadScriptsFromHTML = (htmlContent: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const scripts = doc.querySelectorAll('script');
    scripts.forEach((script) => {
      const scriptSrc = script.getAttribute('src');
      if (scriptSrc) {
        LoadExternalScript(scriptSrc);
      }
    });
  };

  const updateHeader = () => {
    triggerAEMHeaderCartIcon();
  };

  const getHtml = useCallback(async () => {
    const response = await getHeaderFooter();
    if (response.source) {
      setIsSpectrabaseHeader(true);
      // spectrabase returns header/footer HTML and a headerFooterCSS link (string)
      // Extract and load scripts from header and footer HTML content
      loadCSS(response.styles as unknown as string[]);
      extractAndLoadScriptsFromHTML(response.header);
      extractAndLoadScriptsFromHTML(response.footer);
      setState({ header: response.header, footer: response.footer });
    } else {
      setIsSpectrabaseHeader(false);
      // default behaviour for legacy combined header/footer
      updateStyles(response.styles as any);
      updateScripts(response.scripts as any);
      setState({ header: response.header, footer: response.footer });
    }
  }, []);

  const onMounted = useCallback(() => {
    updateHeader();
  }, []);
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isSpectrabaseHeader && stylesLoaded) {
      const override = document.createElement('style');
      override.innerHTML = `
      .main-container {
        padding-top: 0 !important;
      }
    `;
      document.head.appendChild(override);

      return () => {
        document.head.removeChild(override);
      };
    }
  }, [isSpectrabaseHeader, stylesLoaded]);
  useEffect(() => {
    if (!loaded) {
      getHtml();
      loaded = true;
    }
  }, [getHtml]);

  useEffect(() => {
    updateCartCookies(productsTotal);
    updateHeader();
  }, [productsTotal]);

  const payload = useMemo(() => ({
    ...state, stylesLoaded, onMounted,
  }), [state, stylesLoaded, onMounted]);

  return (
    <AEMResourceContext.Provider value={payload}>
      {children}
    </AEMResourceContext.Provider>
  );
};

export const useAEMResource = (): UseAEMResource => {
  const context = useContext(AEMResourceContext);

  if (!context) {
    throw new Error('useAEMResource must be used within a AEMResourceProvider');
  }
  return context;
};
